import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import api from '../utils/api';
import { desglosarIGV, fmt } from '../utils/igv';

const TABS = ['Ventas', 'Inventario'];

export default function StatsPage() {
  const { products, categories, online, flushAndSync, syncing } = useApp();
  const [tab,       setTab]     = useState('Ventas');
  const [sales,     setSales]   = useState([]);
  const [todaySt,   setTodaySt] = useState(null);
  const [selSale,   setSelSale] = useState(null);
  const [selItems,  setSelItems] = useState([]);
  const [selDate,   setSelDate]  = useState(new Date().toISOString().split('T')[0]);
  const [loading,   setLoading]  = useState(false);

  // Cargar ventas cuando cambia la fecha
  useEffect(() => {
    if (!online) return;
    setLoading(true);
    fetch(`/api/sales?date=${selDate}&limit=100`)
      .then(r => r.json())
      .then(d => { setSales(d.sales || []); setTodaySt(d.todayStats); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selDate, online]);

  // Abrir detalle de venta
  const openSale = async (sale) => {
    const d = await fetch(`/api/sales/${sale.id}`).then(r => r.json());
    setSelSale(d.sale);
    setSelItems(d.items || []);
  };

  const local = useMemo(() => {
    const totalConIGV = products.reduce((s, p) => s + p.price * p.stock, 0);
    const { subtotal, igv, total } = desglosarIGV(totalConIGV);
    const low = products.filter(p => p.stock <= (p.min_stock || 5));
    const out = products.filter(p => p.stock === 0);
    const byCatLocal = categories
      .filter(c => c.id !== 'all')
      .map(c => ({
        ...c,
        count: products.filter(p => p.category_id === c.id).length,
        value: products.filter(p => p.category_id === c.id).reduce((s, p) => s + p.price * p.stock, 0),
      }))
      .filter(c => c.count > 0)
      .sort((a, b) => b.value - a.value);
    return { totalConIGV: total, subtotal, igv, total, low, out, byCatLocal };
  }, [products, categories]);

  const maxVal = local.byCatLocal[0]?.value || 1;
  const top5   = [...products].sort((a,b)=>(b.price*b.stock)-(a.price*a.stock)).slice(0,5);

  return (
    <div style={S.wrap}>
      {/* Header */}
      <div style={S.hdr}>
        <div>
          <h2 style={S.title}>Reportes</h2>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <div style={{ width:7, height:7, borderRadius:'50%', background: online?'var(--ok)':'var(--err)' }}/>
            <span style={S.sub}>{online ? 'En línea' : 'Sin conexión'}</span>
          </div>
        </div>
        <button style={S.syncBtn} onClick={flushAndSync} disabled={!online||syncing}>
          {syncing ? '⟳ Sincronizando...' : '⟳ Sincronizar'}
        </button>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {TABS.map(t => (
          <button key={t} style={{ ...S.tab, ...(tab===t ? S.tabOn : {}) }}
            onClick={() => setTab(t)}>
            {t === 'Ventas' ? '🧾 Ventas' : '📦 Inventario'}
          </button>
        ))}
      </div>

      <div style={S.scroll}>

        {/* ═══ TAB VENTAS ═══ */}
        {tab === 'Ventas' && (
          <>
            {/* Selector de fecha */}
            <div style={S.dateRow}>
              <button style={S.dateBtn} onClick={() => {
                const d = new Date(selDate); d.setDate(d.getDate()-1);
                setSelDate(d.toISOString().split('T')[0]);
              }}>‹</button>
              <input type="date" style={S.dateInp} value={selDate}
                onChange={e => setSelDate(e.target.value)}/>
              <button style={S.dateBtn} onClick={() => {
                const d = new Date(selDate); d.setDate(d.getDate()+1);
                setSelDate(d.toISOString().split('T')[0]);
              }}>›</button>
            </div>

            {/* KPIs del día */}
            {todaySt && (
              <div style={S.kpiGrid}>
                <KPI icon="🧾" label="Ventas del día" value={todaySt.cnt || 0} color="var(--teal)"/>
                <KPI icon="💰" label="Total del día"  value={fmt(todaySt.total || 0)} color="var(--acc)"/>
              </div>
            )}

            {/* Lista de ventas */}
            {!online && (
              <div style={S.offlineMsg}>Sin conexión — no se pueden cargar ventas</div>
            )}
            {online && loading && (
              <div style={{ textAlign:'center', padding:40, color:'var(--t2)' }}>Cargando...</div>
            )}
            {online && !loading && sales.length === 0 && (
              <div style={{ textAlign:'center', padding:40, color:'var(--t2)', fontSize:13 }}>
                <p style={{ fontSize:36 }}>🧾</p>
                <p style={{ marginTop:8 }}>Sin ventas en esta fecha</p>
              </div>
            )}
            {online && !loading && sales.length > 0 && (
              <Sec title={`${sales.length} venta${sales.length!==1?'s':''}`}>
                {sales.map(s => (
                  <div key={s.id} style={S.saleRow} onClick={() => openSale(s)}>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:13, fontWeight:700 }}>Venta #{s.id}</p>
                      <p style={{ fontSize:11, color:'var(--t1)', marginTop:2 }}>
                        {s.created_at?.slice(11,16)}h
                        {s.cashier ? ` · ${s.cashier}` : ''}
                      </p>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <p style={{ fontSize:15, fontWeight:800, color:'var(--acc)' }}>{fmt(s.total)}</p>
                      <p style={{ fontSize:10, color:'var(--t2)' }}>IGV {fmt(s.igv)}</p>
                    </div>
                    <span style={{ color:'var(--t2)', fontSize:16, marginLeft:6 }}>›</span>
                  </div>
                ))}
              </Sec>
            )}
          </>
        )}

        {/* ═══ TAB INVENTARIO ═══ */}
        {tab === 'Inventario' && (
          <>
            <div style={S.kpiGrid}>
              <KPI icon="📦" label="Productos"  value={products.length}       color="var(--blue,#4a9eff)"/>
              <KPI icon="💰" label="Valor stock" value={fmt(local.totalConIGV)} color="var(--teal)"/>
              <KPI icon="⚠️" label="Stock bajo"  value={local.low.length}      color="var(--warn)" alert={local.low.length>0}/>
              <KPI icon="❌" label="Agotados"    value={local.out.length}      color="var(--err)"  alert={local.out.length>0}/>
            </div>

            <Sec title="Desglose IGV del stock">
              <div style={S.igvBreak}>
                <div style={S.igvBRow}><span style={S.igvBLbl}>Op. gravadas (sin IGV)</span><span style={S.igvBVal}>{fmt(local.subtotal)}</span></div>
                <div style={S.igvBRow}><span style={S.igvBLbl}>IGV 18%</span><span style={S.igvBVal}>{fmt(local.igv)}</span></div>
                <div style={{ ...S.igvBRow, borderTop:'0.5px solid var(--bd2)', paddingTop:6, marginTop:2 }}>
                  <span style={{ fontSize:14, fontWeight:700 }}>Total con IGV</span>
                  <span style={{ fontSize:14, fontWeight:800, color:'var(--acc)' }}>{fmt(local.total)}</span>
                </div>
              </div>
            </Sec>

            {local.byCatLocal.length > 0 && (
              <Sec title="Por categoría">
                {local.byCatLocal.map(c => (
                  <div key={c.id} style={S.catRow}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                        <span style={{ fontSize:13, fontWeight:600 }}>{c.emoji} {c.name}</span>
                        <span style={{ fontSize:11, color:'var(--t1)' }}>{c.count} prods · {fmt(c.value)}</span>
                      </div>
                      <div style={S.barBg}>
                        <div style={{ ...S.barFill, width:`${(c.value/maxVal)*100}%` }}/>
                      </div>
                    </div>
                  </div>
                ))}
              </Sec>
            )}

            {local.low.length > 0 && (
              <Sec title="⚠️ Reabastecer pronto">
                {local.low.map(p => (
                  <div key={p.id} style={S.alertRow}>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:13, fontWeight:600 }}>{p.name}</p>
                      <p style={{ fontSize:11, color:'var(--t1)' }}>{p.category_name} · mín: {p.min_stock||5}</p>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <p style={{ fontSize:16, fontWeight:800, color: p.stock===0?'var(--err)':'var(--warn)' }}>{p.stock}</p>
                      <p style={{ fontSize:10, color:'var(--t2)' }}>{p.unit}</p>
                    </div>
                  </div>
                ))}
              </Sec>
            )}

            {top5.length > 0 && (
              <Sec title="💎 Mayor valor en stock">
                {top5.map((p, i) => (
                  <div key={p.id} style={S.topRow}>
                    <span style={{ fontSize:11, color:'var(--t2)', width:18, fontWeight:700 }}>#{i+1}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:12, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</p>
                      <p style={{ fontSize:10, color:'var(--t1)' }}>{p.stock} {p.unit} × {fmt(p.price)}</p>
                    </div>
                    <span style={{ fontSize:13, fontWeight:800, color:'var(--acc)' }}>{fmt(p.price*p.stock)}</span>
                  </div>
                ))}
              </Sec>
            )}
          </>
        )}
      </div>

      {/* Modal detalle de venta */}
      {selSale && (
        <div style={S.modalOverlay} onClick={() => setSelSale(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <div>
                <p style={{ fontSize:16, fontWeight:800 }}>Venta #{selSale.id}</p>
                <p style={{ fontSize:11, color:'var(--t1)' }}>{selSale.created_at}</p>
              </div>
              <button style={S.xBtn} onClick={() => setSelSale(null)}>✕</button>
            </div>

            <div style={{ maxHeight:'50vh', overflowY:'auto' }}>
              {selItems.map((item, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'0.5px solid var(--bd)' }}>
                  <div style={{ flex:1 }}>
                    <p style={{ fontSize:13, fontWeight:600 }}>{item.name}</p>
                    <p style={{ fontSize:11, color:'var(--t1)' }}>
                      {fmt(item.price)}/{item.by_weight ? 'kg' : item.unit} ×{' '}
                      {item.by_weight ? `${parseFloat(item.qty.toFixed(3))} kg` : `${item.qty} ${item.unit}`}
                    </p>
                  </div>
                  <p style={{ fontSize:14, fontWeight:700, color:'var(--acc)' }}>{fmt(item.subtotal)}</p>
                </div>
              ))}
            </div>

            <div style={{ marginTop:12, paddingTop:12, borderTop:'0.5px solid var(--bd2)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ fontSize:12, color:'var(--t1)' }}>Sin IGV</span>
                <span style={{ fontSize:12 }}>{fmt(selSale.subtotal)}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                <span style={{ fontSize:12, color:'var(--t1)' }}>IGV 18%</span>
                <span style={{ fontSize:12 }}>{fmt(selSale.igv)}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:16, fontWeight:700 }}>Total</span>
                <span style={{ fontSize:18, fontWeight:800, color:'var(--acc)' }}>{fmt(selSale.total)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const KPI = ({ icon, label, value, color, alert }) => (
  <div style={{ background:'var(--bg2)', borderRadius:12, border:`0.5px solid ${alert?color+'66':'var(--bd)'}`, padding:'14px 10px', display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
    <span style={{ fontSize:22 }}>{icon}</span>
    <p style={{ fontSize:18, fontWeight:800, color, fontFamily:'var(--mono)' }}>{value}</p>
    <p style={{ fontSize:10, color:'var(--t1)', textAlign:'center' }}>{label}</p>
  </div>
);

const Sec = ({ title, children }) => (
  <div style={{ marginBottom:16 }}>
    <p style={{ fontSize:11, fontWeight:700, color:'var(--t1)', textTransform:'uppercase', letterSpacing:.6, marginBottom:8, paddingLeft:2 }}>{title}</p>
    <div style={{ background:'var(--bg2)', borderRadius:12, border:'0.5px solid var(--bd)', overflow:'hidden' }}>{children}</div>
  </div>
);

const S = {
  wrap:       { display:'flex', flexDirection:'column', height:'100%' },
  hdr:        { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 14px 8px', flexShrink:0 },
  title:      { fontSize:20, fontWeight:800 },
  sub:        { fontSize:11, color:'var(--t1)' },
  syncBtn:    { background:'var(--bg2)', border:'0.5px solid var(--bd2)', color:'var(--t0)', borderRadius:10, padding:'7px 12px', fontSize:12 },
  tabs:       { display:'flex', gap:6, padding:'0 14px 10px', flexShrink:0 },
  tab:        { flex:1, background:'var(--bg2)', border:'0.5px solid var(--bd2)', borderRadius:10, padding:'9px 6px', fontSize:13, fontWeight:600, color:'var(--t1)' },
  tabOn:      { background:'var(--acc)', color:'#fff', border:'none' },
  scroll:     { flex:1, overflowY:'auto', padding:'4px 14px 80px', scrollbarWidth:'none' },
  kpiGrid:    { display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 },
  dateRow:    { display:'flex', alignItems:'center', gap:8, marginBottom:14 },
  dateBtn:    { background:'var(--bg2)', border:'0.5px solid var(--bd2)', borderRadius:8, width:36, height:36, fontSize:18, color:'var(--t0)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  dateInp:    { flex:1, background:'var(--bg2)', border:'0.5px solid var(--bd2)', borderRadius:8, padding:'8px 10px', color:'var(--t0)', fontSize:13, textAlign:'center' },
  saleRow:    { display:'flex', alignItems:'center', gap:10, padding:'12px 14px', borderBottom:'0.5px solid var(--bd)', cursor:'pointer' },
  offlineMsg: { textAlign:'center', padding:20, color:'var(--t2)', fontSize:13 },
  igvBreak:   { padding:'12px 14px' },
  igvBRow:    { display:'flex', justifyContent:'space-between', marginBottom:6 },
  igvBLbl:    { fontSize:12, color:'var(--t1)' },
  igvBVal:    { fontSize:12, fontWeight:600 },
  catRow:     { padding:'10px 14px', borderBottom:'0.5px solid var(--bd)' },
  barBg:      { height:6, background:'var(--bg3)', borderRadius:3, overflow:'hidden' },
  barFill:    { height:'100%', background:'var(--acc)', borderRadius:3, transition:'width .6s ease' },
  alertRow:   { display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderBottom:'0.5px solid var(--bd)' },
  topRow:     { display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderBottom:'0.5px solid var(--bd)' },
  modalOverlay:{ position:'fixed', inset:0, background:'rgba(0,0,0,.8)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:200 },
  modalBox:   { background:'var(--bg1)', width:'100%', maxWidth:500, borderRadius:'20px 20px 0 0', padding:20, maxHeight:'80vh', display:'flex', flexDirection:'column' },
  xBtn:       { background:'var(--bg2)', border:'0.5px solid var(--bd)', borderRadius:8, padding:'4px 10px', fontSize:13, color:'var(--t1)' },
};
